/**
 * Cognito PKCE flow client + helpers.
 *
 * Pairs with the backend route ``POST /api/auth/cognito/callback``
 * (see ``src/flowstack/server/routes/cognito_auth.py``). Wire shape:
 *
 *   1. SPA calls :func:`startCognitoLogin` — generates a PKCE
 *      ``code_verifier`` + SHA256 ``code_challenge`` + random
 *      ``state``, stores ``code_verifier`` and ``state`` in
 *      ``sessionStorage`` (NOT localStorage — survives a tab refresh,
 *      not a tab close, and is per-origin so a sibling subdomain
 *      can't read it), then redirects the user to the Cognito Hosted
 *      UI's ``/oauth2/authorize`` endpoint with the challenge.
 *   2. User authenticates at the Hosted UI. Cognito 302-redirects
 *      back to the SPA's ``/auth/callback`` with ``code`` and
 *      ``state`` query params (and an ``error`` param on cancel /
 *      bad input).
 *   3. The ``/auth/callback`` route reads ``code`` + ``state`` from
 *      the URL, calls :func:`exchangeCognitoCallback` which:
 *
 *      a. Verifies the returned ``state`` matches the stored value —
 *         CSRF defense; without it a forged callback URL could land
 *         a session for a different user.
 *      b. POSTs ``{code, code_verifier, redirect_uri}`` to the
 *         backend, which exchanges with Cognito and mints the
 *         FlowStack session cookie.
 *      c. Clears the stored ``code_verifier`` + ``state`` so they
 *         can't be replayed.
 *
 * Config (build-time, via Vite ``VITE_*`` env vars):
 *
 *   - ``VITE_COGNITO_DOMAIN`` — Hosted UI domain
 *     (e.g. ``attmos-customers.auth.us-east-1.amazoncognito.com``).
 *     Domain only, no scheme. Required when cognito is the active
 *     auth mode.
 *   - ``VITE_COGNITO_CLIENT_ID`` — App client ID from the
 *     ``attmos-customers`` pool. The single shared ATTMOS user pool
 *     gives each app its own *app client*; this is FlowStack's.
 *   - ``VITE_COGNITO_REDIRECT_URI`` — the SPA callback URL. Must
 *     exactly match one of the app client's registered callback
 *     URLs in Cognito. Default in this module assumes the
 *     production deploy; local-dev overrides via env.
 *
 * When any of the three are unset / blank, :func:`isCognitoConfigured`
 * returns ``false`` and the GUI falls back to the pilot login form.
 * That lets a developer ``pnpm dev`` against the pilot backend
 * without standing up a Cognito client.
 */

import { ApiError, apiFetch } from "@/api/client";
import type { PilotSession } from "@/api/auth";

// ---------- Configuration ------------------------------------------

export interface CognitoConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  /** OpenID scopes the SPA requests. Must be a superset of what the
   *  app client is configured to accept. ``openid`` is required for
   *  Cognito to issue an id_token; ``email`` + ``profile`` populate
   *  the session's display fields. */
  scopes: string[];
}

const _CONFIG_OVERRIDE: { value: CognitoConfig | null } = { value: null };

/** Test-only: pin a config so unit tests don't depend on
 *  ``import.meta.env``. Production code calls :func:`getCognitoConfig`. */
export function _setCognitoConfigForTesting(config: CognitoConfig | null): void {
  _CONFIG_OVERRIDE.value = config;
}

function _readEnv(name: string): string | undefined {
  // ``import.meta.env`` is the Vite-injected build-time env. The
  // values are baked into the bundle at ``pnpm build`` time; runtime
  // changes don't propagate. App Runner sets ``FLOWSTACK_*`` for the
  // backend; the SPA build step is where ``VITE_*`` lands.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  if (!env) return undefined;
  const raw = env[name];
  if (!raw) return undefined;
  const trimmed = raw.trim();
  return trimmed === "" ? undefined : trimmed;
}

/** Read the cognito config from ``import.meta.env`` (or the test
 *  override). Returns ``null`` when any required var is unset so
 *  callers can render the pilot fallback. */
export function getCognitoConfig(): CognitoConfig | null {
  if (_CONFIG_OVERRIDE.value !== null) return _CONFIG_OVERRIDE.value;
  const domain = _readEnv("VITE_COGNITO_DOMAIN");
  const clientId = _readEnv("VITE_COGNITO_CLIENT_ID");
  const redirectUri = _readEnv("VITE_COGNITO_REDIRECT_URI");
  if (!domain || !clientId || !redirectUri) return null;
  return {
    domain,
    clientId,
    redirectUri,
    // ``aws.cognito.signin.user.admin`` is REQUIRED for the MFA proxy
    // path (#597 Phase 2): backend calls ``GetUser`` /
    // ``AssociateSoftwareToken`` / ``StartWebAuthnRegistration`` /
    // ``ListWebAuthnCredentials`` / ``SetUserMFAPreference`` on the
    // signed-in user's behalf with the stored access token, and those
    // ``cognito-idp`` self-APIs reject any token that lacks this scope
    // with ``NotAuthorizedException`` (which the backend maps to 401
    // ``cognito_token_invalid``).
    //
    // Least-privilege tradeoff (Rupert PR-610 r0 ADVISORY #1, accepted).
    // Cognito's hosted-UI PKCE flow issues ONE access token per
    // session — Cognito does not natively support a narrower everyday
    // token + a wider MFA-only token via clean token-exchange, so
    // requesting a narrower token for non-MFA use would need a full
    // second OAuth round-trip on Settings → Security entry. Not worth
    // the added complexity because ``aws.cognito.signin.user.admin``
    // ONLY authorizes self-service operations the user could already
    // perform on THEIR OWN account (GetUser, own-MFA setup, own-
    // WebAuthn management) — it is NOT admin over the pool nor over
    // other users. Residual risk: a leaked access token (XSS)
    // could mutate the victim's OWN MFA config; the FlowStack
    // session cookie is HttpOnly + SameSite=Lax and the Cognito
    // access token itself is kept server-side (never surfaces to
    // JS — Phase 1's #597 storage design), so the XSS blast radius
    // is limited to what the browser can already do via the SPA.
    //
    // The FlowStack app client's ``AllowedOAuthScopes`` must include
    // this value — see ``RESOURCES.md`` "Cognito app-client scope
    // for FlowStack (post-#597)" for the provisioning checklist that
    // any new FlowStack environment must follow.
    scopes: ["openid", "email", "profile", "aws.cognito.signin.user.admin"],
  };
}

export function isCognitoConfigured(): boolean {
  return getCognitoConfig() !== null;
}

// ---------- PKCE primitives ----------------------------------------

const _VERIFIER_BYTES = 32;
const _STATE_BYTES = 16;
const _VERIFIER_STORAGE_KEY = "flowstack.cognito.code_verifier";
const _STATE_STORAGE_KEY = "flowstack.cognito.state";
const _RETURN_TO_STORAGE_KEY = "flowstack.cognito.return_to";

/** Set by ``useLogout`` after a successful logout; read once by
 *  :class:`CognitoSignInPage` to decide between auto-redirect (cold
 *  visit) and the explicit "you're signed out" card. Same mechanism
 *  pharmaforge uses (its ``JUST_SIGNED_OUT_FLAG``) for the same reason:
 *  Cognito's session cookie outlives our FlowStack-side logout, so an
 *  auto-redirect post-logout would land an immediate silent re-SSO
 *  and the user would never see they signed out. */
export const JUST_SIGNED_OUT_FLAG = "flowstack.cognito.just_signed_out";

/** Persist a 1 in sessionStorage so the next ``/auth/cognito/sign-in``
 *  render shows the explicit card + forces ``prompt=login``. Called
 *  from the logout mutation. Swallows DOMException — a sandboxed
 *  iframe / disabled storage just falls back to the auto-redirect
 *  path, which is wrong but not broken. */
export function markJustSignedOut(): void {
  try {
    window.sessionStorage.setItem(JUST_SIGNED_OUT_FLAG, "1");
  } catch {
    // sessionStorage unavailable; the SignIn page will auto-redirect.
  }
}

/** Read the post-logout flag WITHOUT clearing it. Used by the
 *  session fetcher to suppress the trusted-browser silent refresh
 *  right after a sign-out — otherwise the refresh would re-mint a
 *  session before the server-side revocation is observable and the
 *  Sign out click would appear to do nothing. Clearing stays the
 *  sign-in page's job (:func:`takeJustSignedOutFlag`). */
export function peekJustSignedOut(): boolean {
  try {
    return window.sessionStorage.getItem(JUST_SIGNED_OUT_FLAG) === "1";
  } catch {
    return false;
  }
}

/** Read + clear the post-logout flag atomically. Returns whether the
 *  caller should render the "you're signed out" card. */
export function takeJustSignedOutFlag(): boolean {
  try {
    const flag = window.sessionStorage.getItem(JUST_SIGNED_OUT_FLAG);
    window.sessionStorage.removeItem(JUST_SIGNED_OUT_FLAG);
    return flag === "1";
  } catch {
    return false;
  }
}

function _base64UrlEncode(bytes: Uint8Array): string {
  // RFC 7636 §4.1: base64url *without* padding.
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Generate a fresh PKCE ``code_verifier`` — 32 random bytes
 *  base64url-encoded → 43 chars, the lower bound RFC 7636 §4.1 sets.
 *  Each login MUST get a fresh verifier; reusing one is what the
 *  backend test ``test_callback_returns_401_when_token_endpoint_returns_400``
 *  exercises against. */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(_VERIFIER_BYTES);
  crypto.getRandomValues(bytes);
  return _base64UrlEncode(bytes);
}

/** SHA256(verifier) → base64url. The Hosted UI's ``code_challenge``
 *  query param. */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return _base64UrlEncode(new Uint8Array(digest));
}

/** Random ``state`` — CSRF defense. The Hosted UI echoes it back on
 *  the callback; the SPA verifies the returned value matches. */
export function generateState(): string {
  const bytes = new Uint8Array(_STATE_BYTES);
  crypto.getRandomValues(bytes);
  return _base64UrlEncode(bytes);
}

// ---------- Storage ------------------------------------------------
//
// sessionStorage is the right choice because:
//
// - It survives a redirect away to Cognito + back (the whole point).
// - It does NOT survive a tab close, so a stolen browser tab handed
//   to another user later can't replay a stale verifier.
// - It is origin-scoped; a sibling subdomain (e.g.
//   ``flowstack-api.attmosdiscovery.com``) cannot read it even
//   though it shares the eTLD+1 with the SPA.
//
// localStorage would persist across tab close and let an attacker
// who briefly steals a tab carry the verifier away. Cookies would
// ride along on every backend request which is a privacy leak.
//
// Tests inject a custom Storage by passing it in; the default
// resolves ``window.sessionStorage`` lazily so SSR / node-without-DOM
// tests can stub it without throwing at module import.

function _defaultStorage(): Storage {
  if (typeof window === "undefined" || !window.sessionStorage) {
    throw new Error("cognito helpers require sessionStorage; this code path is browser-only");
  }
  return window.sessionStorage;
}

interface StashedPkceState {
  verifier: string;
  state: string;
  /** Where to navigate after a successful exchange — the page the
   *  user was on when the redirect kicked off. */
  returnTo: string;
}

function _writeStash(stash: StashedPkceState, storage: Storage): void {
  storage.setItem(_VERIFIER_STORAGE_KEY, stash.verifier);
  storage.setItem(_STATE_STORAGE_KEY, stash.state);
  storage.setItem(_RETURN_TO_STORAGE_KEY, stash.returnTo);
}

/** Sanitize a candidate ``returnTo`` to a same-origin relative path.
 *
 *  Per Rupert PR-328 r1 #1+#5: ``returnTo`` flows from
 *  ``location.state.returnTo`` which an attacker-influenced
 *  navigation could populate with an absolute URL
 *  (``https://evil.com``) or a protocol-relative URL (``//evil.com``).
 *  React Router's ``navigate(absoluteUrl)`` is a full-document
 *  navigation, so a crafted ``returnTo`` would land an authenticated
 *  user on an attacker-controlled origin post-login — open redirect,
 *  useful for phishing and token-leak chains.
 *
 *  Allow only paths that start with exactly one ``/``. Anything else
 *  falls back to ``/projects`` (the default landing surface).
 *  Applied both at stash time and at use time so a future code path
 *  that bypasses one site is still caught at the other.
 */
export function sanitizeReturnTo(raw: string | undefined | null): string {
  if (!raw) return "/projects";
  // Reject protocol-relative ``//evil.com`` (browser treats as
  // absolute), absolute URLs with a scheme, and anything that
  // doesn't start with a single ``/``.
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/projects";
  // Reject ``/\evil.com`` etc. — some browsers normalize ``\`` to
  // ``/`` in URLs, which could escape the relative-path constraint.
  if (raw.includes("\\")) return "/projects";
  return raw;
}

function _readStash(storage: Storage): StashedPkceState | null {
  const verifier = storage.getItem(_VERIFIER_STORAGE_KEY);
  const state = storage.getItem(_STATE_STORAGE_KEY);
  const returnTo = storage.getItem(_RETURN_TO_STORAGE_KEY) ?? "/";
  if (!verifier || !state) return null;
  return { verifier, state, returnTo };
}

function _clearStash(storage: Storage): void {
  storage.removeItem(_VERIFIER_STORAGE_KEY);
  storage.removeItem(_STATE_STORAGE_KEY);
  storage.removeItem(_RETURN_TO_STORAGE_KEY);
}

// ---------- Login (redirect to Hosted UI) --------------------------

export interface StartCognitoLoginOptions {
  config?: CognitoConfig | null;
  /** Where to send the user after a successful exchange. The
   *  callback route reads this back out of storage. */
  returnTo?: string;
  /** Pass ``"login"`` to add ``prompt=login`` to the authorize URL.
   *  Forces the Hosted UI to render the sign-in form regardless of
   *  Cognito session state — the only reliable way to defeat the
   *  silent SSO chain after a FlowStack-side logout (pharmaforge
   *  documented the same in #101). Default omits the param so a cold
   *  visit can silently re-SSO when a valid Cognito session exists. */
  prompt?: "login";
  /** Test seam — tests inject a fake Storage so they can assert on
   *  what got written. */
  storage?: Storage;
  /** Test seam — tests inject a fake redirector instead of mutating
   *  ``window.location.href``. */
  redirect?: (url: string) => void;
}

/** Mint PKCE state, stash it, then redirect to the Hosted UI's
 *  ``/oauth2/authorize`` endpoint. Never returns under normal flow
 *  (the redirect is the exit). Throws when the config is unset; the
 *  caller should check :func:`isCognitoConfigured` before calling. */
export async function startCognitoLogin(opts: StartCognitoLoginOptions = {}): Promise<void> {
  // Per Rupert PR-328 r1 #8: ``config: null`` explicitly opts out of
  // the env-var fallback so tests can pin the unconfigured branch
  // regardless of which ``VITE_COGNITO_*`` values are on the build.
  // ``opts.config ?? getCognitoConfig()`` would have made
  // ``config: null`` equivalent to "use env" — exactly the opposite.
  const config = opts.config !== undefined ? opts.config : getCognitoConfig();
  if (!config) {
    throw new Error("startCognitoLogin called without a configured Cognito environment");
  }
  const storage = opts.storage ?? _defaultStorage();
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();
  _writeStash({ verifier, state, returnTo: sanitizeReturnTo(opts.returnTo) }, storage);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes.join(" "),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  if (opts.prompt) {
    params.set("prompt", opts.prompt);
  }
  const url = `https://${config.domain}/oauth2/authorize?${params.toString()}`;

  // Tests inject opts.redirect; production assigns ``window.location.href``.
  // ``replace()`` (vs ``assign()``) keeps the broken login attempt
  // out of the back-button history so a frustrated user mashing
  // "back" doesn't replay the redirect loop.
  if (opts.redirect) {
    opts.redirect(url);
    return;
  }
  window.location.replace(url);
}

// ---------- Callback exchange --------------------------------------

export interface ExchangeCognitoCallbackOptions {
  /** Query string from the Cognito redirect. Caller passes
   *  ``window.location.search`` (or a stub for tests). */
  search: string;
  config?: CognitoConfig | null;
  storage?: Storage;
}

export interface ExchangeCognitoCallbackResult {
  session: PilotSession;
  returnTo: string;
}

/** Error class so the callback route can render a clear message
 *  for each failure mode without parsing strings. */
export class CognitoCallbackError extends Error {
  constructor(
    public readonly code:
      | "missing_state"
      | "state_mismatch"
      | "missing_code"
      | "cognito_error"
      | "no_stash"
      | "unconfigured",
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = "CognitoCallbackError";
  }
}

/** Verify the callback URL + stored PKCE state, then exchange the
 *  ``code`` with the backend. Clears storage on every exit path so a
 *  replay of the same callback URL can't double-spend the verifier. */
export async function exchangeCognitoCallback(
  opts: ExchangeCognitoCallbackOptions
): Promise<ExchangeCognitoCallbackResult> {
  // Per Rupert PR-328 r1 #8/#9: same explicit ``null`` vs ``undefined``
  // distinction as :func:`startCognitoLogin` so the unconfigured
  // branch is force-able from tests.
  const config = opts.config !== undefined ? opts.config : getCognitoConfig();
  // Resolve storage *before* the unconfigured branch so we can
  // honor the "every callback exit clears the stash" invariant on
  // that path too — per Rupert PR-328 r2 #1 the unconfigured exit
  // was the last hole in that contract.
  const storage = opts.storage ?? _defaultStorage();
  if (!config) {
    _clearStash(storage);
    throw new CognitoCallbackError(
      "unconfigured",
      "Cognito env vars are not set on this build; cannot complete callback."
    );
  }
  const params = new URLSearchParams(opts.search);

  // Cognito surfaces user-cancellation + Hosted-UI failures via
  // ``error`` + ``error_description`` query params on the redirect.
  // Same recovery as any other failure: clear stash + tell the user.
  const cognitoError = params.get("error");
  if (cognitoError) {
    _clearStash(storage);
    const desc = params.get("error_description") ?? cognitoError;
    throw new CognitoCallbackError("cognito_error", `Cognito sign-in failed: ${desc}`);
  }

  const code = params.get("code");
  const returnedState = params.get("state");
  const stash = _readStash(storage);

  if (!stash) {
    // No verifier stored → either the user opened the callback URL
    // directly, the storage was cleared mid-flow, or this is a tab
    // that wasn't the one that started login. All paths recover by
    // re-running the login. Per Rupert PR-328 r1 #10: clear here too
    // so a partially-corrupted stash (e.g. just ``return_to`` or
    // ``state`` left over from a prior aborted run) doesn't linger.
    _clearStash(storage);
    throw new CognitoCallbackError(
      "no_stash",
      "Sign-in session expired or this tab didn't start the flow. Try again."
    );
  }
  // Whatever happens below, the stash is now single-use; clear
  // before doing any further work so a thrown exception still
  // invalidates it.
  _clearStash(storage);

  if (!returnedState) {
    throw new CognitoCallbackError("missing_state", "Cognito callback URL is missing ``state``.");
  }
  if (returnedState !== stash.state) {
    // Forged callback URL or a stale tab. Refuse before touching the
    // backend so we never accept a code that didn't originate from
    // this tab's authorize call.
    throw new CognitoCallbackError(
      "state_mismatch",
      "Cognito callback ``state`` did not match. Try signing in again."
    );
  }
  if (!code) {
    throw new CognitoCallbackError("missing_code", "Cognito callback URL is missing ``code``.");
  }

  // The backend mints the session cookie + returns the same
  // ``SessionResponse`` shape pilot login returns. We surface
  // ``ApiError`` as-is so the callback page can show the
  // backend's ``error_code`` directly.
  let session: PilotSession;
  try {
    session = await apiFetch<PilotSession>("/api/auth/cognito/callback", {
      method: "POST",
      json: {
        code,
        code_verifier: stash.verifier,
        redirect_uri: config.redirectUri,
      },
    });
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new CognitoCallbackError(
      "cognito_error",
      "Could not reach the FlowStack server. Try again in a moment.",
      err
    );
  }
  return { session, returnTo: stash.returnTo };
}
